import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";
import { FaqSection } from "../components/FaqSection";
import { VALUES, IMAGES } from "../data/content";

const About = () => {
  useEffect(() => { document.title = "About | Iron Shield Veterans Reset Center"; }, []);
  return (
    <>
      <PageHero
        eyebrow="About"
        lines={["Built for the veterans", "the system leaves behind."]}
        intro="Iron Shield exists to give veterans in crisis something in short supply: a safe, structured place to reset — and a person who stays with them until they're stable."
      />

      {/* Mission */}
      <section className="py-20 md:py-28 bg-offwhite" data-testid="about-mission">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Our mission" title="Protection, dignity, and a path forward." />
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-6 text-charcoal/80 md:text-lg leading-relaxed max-w-2xl">
                <p>Too many veterans leave service and, somewhere along the way, lose their footing. A job ends. A relationship breaks. An injury lingers. The fall from stable to homeless can be quick — and the systems meant to catch it are often slow, fragmented, and hard to navigate alone.</p>
                <p>Iron Shield is a deliberate answer to that. We provide transitional housing that feels safe rather than institutional, a stabilization plan that replaces chaos with structure, and case management that treats each veteran as a person rebuilding — not a file to close.</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative aspect-[4/5] overflow-hidden border border-softgray">
                <img src={IMAGES.portraitAlt} alt="A veteran in a moment of reflection" className="h-full w-full object-cover" />
                <div className="absolute -bottom-px -right-px h-16 w-16 border-b-2 border-r-2 border-gold" aria-hidden="true" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-20 md:py-28 bg-navy text-offwhite" data-testid="about-problem">
        <div className="container-x">
          <SectionHeading light eyebrow="The problem" title="Homelessness among veterans is preventable — and predictable." intro="On any given night, tens of thousands of veterans experience homelessness in America. Many more sit just one setback away from it." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { stat: "Structure gap", body: "The hardest moment isn't the crisis — it's the disorienting weeks after, with no routine, no plan, and no single point of contact." },
              { stat: "System maze", body: "Benefits, housing, and healthcare live in separate silos. Navigating them alone, in crisis, is where too many veterans give up." },
              { stat: "Trust deficit", body: "Institutional settings can feel like more of the same. Veterans need a place that earns trust through safety and respect." },
            ].map((c, i) => (
              <Reveal key={c.stat} delay={i * 0.1}>
                <div className="i-card-dark p-8 h-full">
                  <div className="font-heading text-xl font-bold text-gold">{c.stat}</div>
                  <p className="mt-4 text-offwhite/75 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Founding vision */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-vision">
        <div className="container-x">
          <SectionHeading eyebrow="Founding vision" title="A shield is not a weapon. It's protection." />
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-charcoal/80 md:text-lg leading-relaxed">
              Iron Shield was founded on a simple belief: the discipline and structure that carried veterans through service can carry them through recovery — if someone rebuilds the foundation with them. We are veteran-centered without leaning on clichés, serious without being cold, and hopeful without pretending the work is easy.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Founder's Note */}
      <section className="py-20 md:py-28 bg-navy text-offwhite" data-testid="about-founder">
        <div className="container-x">
          <div className="max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 rounded-full border border-offwhite/20 px-4 py-1.5 label-caps text-gold">Founder's Note</span>
            <blockquote className="mt-8 space-y-6 text-offwhite/85 md:text-lg leading-relaxed">
              <p className="relative">
                <span className="font-heading text-6xl text-gold/40 leading-none absolute -top-6 -left-2 select-none" aria-hidden="true">"</span>
                Iron Shield Veterans Reset Center was created in response to a reality I've lived: as a disabled veteran who has experienced hard times after job loss, I've seen how quickly stability can unravel — and how few structured, trustworthy options exist when it does. Iron Shield is being built to help meet that gap through a mission grounded in safety, dignity, accountability, and practical support for veterans facing crisis and instability.
              </p>
              <p>
                This organization is not being built around personality or image. It is being built to stand on clear purpose, responsible stewardship, and a long-term commitment to serving veterans with seriousness and respect.
              </p>
            </blockquote>
            <div className="mt-8 flex items-center gap-4">
              <div className="h-px w-12 bg-gold" />
              <div>
                <div className="font-heading text-lg font-bold text-offwhite">Founder</div>
                <div className="text-sm text-offwhite/60">Iron Shield Veterans Reset Center · U.S. Military Veteran</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-offwhite border-y border-softgray" data-testid="about-values">
        <div className="container-x">
          <SectionHeading eyebrow="Our values" title="Four words we're held to." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-softgray border border-softgray">
            {VALUES.map((v, i) => (
              <Reveal key={v} delay={i * 0.08} className="bg-offwhite">
                <div className="p-8 h-full">
                  <span className="font-heading text-sm font-black text-gold">0{i + 1}</span>
                  <h3 className="mt-4 font-heading text-2xl font-bold text-navy">{v}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 md:py-28 bg-white" data-testid="about-leadership">
        <div className="container-x">
          <SectionHeading eyebrow="Leadership & advisory" title="Built with veterans, clinicians, and community." intro="Our advisory team brings together lived military experience, trauma-informed clinical practice, and nonprofit operations. Full leadership bios will be published as Iron Shield's 501(c)(3) status is finalized." />
        </div>
      </section>

      {/* FAQ */}
      <FaqSection />

      <CTABand />
    </>
  );
};

export default About;
